/* eslint-disable react/jsx-handler-names */
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';

// removed unused classNames import
import KebabMenu from '../../../../../../KebabMenu';
import TextInput from '../../../../../../Form/TextInput';
import FormDropdown from '../../../../../../Form/FormDropdown';
import SidePopup from '../../../../SidePopup';
import { COUNTRY_OPTIONS } from '../../../../../../../constants/main';
import useSwitchableRowData from '../../../../../../../hooks/useSwitchableRowData';
import { UiContext } from '../../../../../../../context/UiContext';
import AdminService from '../../../../../../../services/AdminService';
import classes from './styles.module.scss';
import ManagerSidePanel from '../ManagerSidePanel';
import LocationSidePanel from '../LocationSidePanel';
import getResizedImageLogoUrl from '../../../../../../../helpers/getResizedImageLogoUrl';
import yupSchemaToFields from '../../../../../../../helpers/yupSchemaToFields';
import Textarea from '../../../../../../Form/Textarea';

export default function AddOrEditCompany({
  isVisible,
  handleClose,
  refetch,
  showAgain,
  isEdit,
  isAdd,
  selectedRows,
  companies,
  hasNoAnimation,
}) {
  const [isTriedToSubmit, setIsTriedToSubmit] = useState(false);
  const [locations, setLocations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isLocationPanelVisible, setIsLocationPanelVisible] = useState(false);
  const [editingLocationIndex, setEditingLocationIndex] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [isEditingInitialLogo, setIsEditingInitialLogo] = useState(false);
  const [isLogoRemoved, setIsLogoRemoved] = useState(false);
  const fileInputRef = useRef();

  const { t } = useTranslation();

  const typeOptions = useMemo(
    () => [
      { label: t('common.type.Employer'), value: 'Employer' },
      { label: t('common.type.Agency'), value: 'Agency' },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { label: t('common.status.Active'), value: 'Active' },
      { label: t('common.status.Prospect'), value: 'Prospect' },
      { label: t('common.status.Inactive'), value: 'Inactive' },
    ],
    [t]
  );

  const {
    showModal,
    showUnknownErrorModal,
    setIsFetching,
    setIsCreatingOrUpdating,
    isCreatingOrUpdating,
  } = useContext(UiContext);
  const { currentDataIndex, switchToPrevDataElement, switchToNextDataElement } =
    useSwitchableRowData(companies, selectedRows);

  // Locations Logic (Edit mode only, via separate endpoints)
  const fetchLocations = async () => {
    try {
      const companyId = companies?.[currentDataIndex]?.id;
      if (!companyId) return;
      const data = await AdminService.getLocations({
        pageNumber: 1,
        pageSize: 9999,
        Filters: `company.id:eq:${companyId}`,
      });
      setLocations(data?.data || data || []);
    } catch (error) {
      // Fail silently in UI and keep empty
    }
  };

  const createCompany = async (values) => {
    try {
      setIsFetching(true);
      setIsCreatingOrUpdating(true);

      let uploadedLogo;
      if (logoFile) {
        uploadedLogo = await AdminService.uploadImage(logoFile, 'company');
      }

      const requestData = {
        name: values.name,
        type: values.type.value,
        vatId: values.vatId || null,
        status: values.status.value,
        note: values.note || null,
        address: values.address,
        country: values.country.value,
        industryId: values.industry.value,
        logo: uploadedLogo ? uploadedLogo.fileName : null,
        companyJoiningNote: values.companyJoiningNote || null,
        companyDescription: values.companyDescription || null,
      };

      await AdminService.createCompany(requestData);
      await refetch();
      await fetchLocations();
      showModal({
        title: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successTitle'
        ),
        text: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successMessageAdd'
        ),
        dismissButtonLabel: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.addMore'
        ),
        confirmButtonLabel: t('common.gotIt'),
        onConfirm: () => {},
        onCancel: showAgain,
      });
      handleClose();
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
      setIsCreatingOrUpdating(false);
    }
  };

  const updateCompany = async (values) => {
    try {
      setIsFetching(true);
      setIsCreatingOrUpdating(true);

      let uploadedLogo;
      if (logoFile) {
        uploadedLogo = await AdminService.uploadImage(logoFile, 'company');
      }

      let nextLogo = companies?.[currentDataIndex]?.logo || null;
      if (uploadedLogo) {
        nextLogo = uploadedLogo.fileName;
      } else if (isLogoRemoved) {
        nextLogo = null;
      }

      const requestData = {
        id: companies?.[currentDataIndex]?.id,
        name: values.name,
        type: values.type.value,
        vatId: values.vatId || null,
        status: values.status.value,
        note: values.note || null,
        address: values.address,
        industryId: values.industry.value,
        country: values.country.value,
        logo: nextLogo,
        companyJoiningNote: values.companyJoiningNote || null,
        companyDescription: values.companyDescription || null,
      };

      await AdminService.updateCompany(requestData);
      await refetch();
      await fetchLocations();
      showModal({
        title: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successTitle'
        ),
        text: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successMessageEdit'
        ),
        dismissButtonLabel: t('common.gotIt'),
        dismissButtonVariant: ' ',
      });
      setIsTriedToSubmit(false);
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
      setIsCreatingOrUpdating(false);
    }
  };

  // Managers Logic (Edit mode only, via separate endpoints)
  const fetchManagers = async () => {
    try {
      const companyId = companies?.[currentDataIndex]?.id;
      if (!companyId) return;
      const data = await AdminService.getCompanyManagers({ id: companyId });
      setManagers(data?.data || data || []);
    } catch (error) {
      // Fail silently in UI and keep empty
    }
  };

  const country = useMemo(
    () =>
      COUNTRY_OPTIONS.find(
        (c) => c.value === companies?.[currentDataIndex]?.country
      ),
    [companies, currentDataIndex]
  );

  const type = useMemo(
    () =>
      typeOptions.find(
        (tp) => tp.value === companies?.[currentDataIndex]?.type
      ),
    [companies, currentDataIndex, typeOptions]
  );

  const status = useMemo(
    () =>
      statusOptions.find(
        (st) => st.value === companies?.[currentDataIndex]?.status
      ),
    [companies, currentDataIndex, statusOptions]
  );

  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .trim()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        address: yup
          .string()
          .trim()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        type: yup
          .object()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        status: yup
          .object()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        country: yup
          .object()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        industry: yup
          .object()
          .required(
            t(
              'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.required'
            )
          ),
        vatId: yup.string().trim().nullable(true),
        note: yup.string().trim().nullable(true),
        companyJoiningNote: yup.string().trim().nullable(true),
        companyDescription: yup.string().trim().nullable(true),
      }),
    [t]
  );

  const convertDataFieldToDropdownValue = (field) => {
    if (!field) {
      return '';
    }
    return { label: field.name, value: field.id };
  };

  const formik = useFormik({
    initialValues: {
      name: isAdd ? '' : companies?.[currentDataIndex]?.name,
      type: isAdd ? '' : type,
      status: isAdd ? '' : status,
      industry: isAdd
        ? ''
        : convertDataFieldToDropdownValue(
            companies?.[currentDataIndex]?.industry
          ),
      vatId: isAdd ? '' : companies?.[currentDataIndex]?.vatId,
      note: isAdd ? '' : companies?.[currentDataIndex]?.note,
      address: isAdd ? '' : companies?.[currentDataIndex]?.address,
      country: isAdd ? '' : country,
      companyJoiningNote: isAdd
        ? ''
        : companies?.[currentDataIndex]?.companyJoiningNote,
      companyDescription: isAdd
        ? ''
        : companies?.[currentDataIndex]?.companyDescription,
    },
    validationSchema,
    onSubmit: isAdd ? createCompany : updateCompany,
    enableReinitialize: true,
  });

  const [isManagerPanelVisible, setIsManagerPanelVisible] = useState(false);
  const [editingManagerIndex, setEditingManagerIndex] = useState(null);

  const openAddManager = () => {
    setEditingManagerIndex(null);
    setIsManagerPanelVisible(true);
  };

  const openEditManager = (idx) => {
    setEditingManagerIndex(idx);
    setIsManagerPanelVisible(true);
  };

  const saveManager = async (managerData) => {
    try {
      setIsFetching(true);
      const companyId = companies?.[currentDataIndex]?.id;
      const payload = {
        firstName: managerData.firstName,
        lastName: managerData.lastName,
        email: managerData.email,
        phone: managerData.phone || null,
        companyId,
        designation: managerData.designation || null,
      };
      if (editingManagerIndex === null || editingManagerIndex === undefined) {
        await AdminService.createCompanyManager(payload);
      } else {
        const id = managers?.[editingManagerIndex]?.id;
        await AdminService.updateCompanyManager({ id, ...payload });
      }
      showModal({
        title: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successTitle'
        ),
        text: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successMessageEdit'
        ),
        dismissButtonLabel: t('common.gotIt'),
        dismissButtonVariant: ' ',
      });
      await fetchManagers();
    } catch (error) {
      if (error?.response?.status === 409) {
        showModal({
          title: t(
            'dashboardComponents.Console.CompanyManagement.addEdit.errorMessages.managerAlreadyExistsTitle'
          ),
          text: error?.response?.data?.message,
          dismissButtonLabel: t('common.gotIt'),
          dismissButtonVariant: 'modal',
        });
      } else {
        showUnknownErrorModal();
      }
    } finally {
      setIsFetching(false);
    }
  };

  const deleteManager = async (idx) => {
    try {
      setIsFetching(true);
      const id = managers?.[idx]?.id;
      await AdminService.deleteCompanyManager({ id });
      await fetchManagers();
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const grantManagerAccess = async (idx) => {
    try {
      setIsFetching(true);
      const id = managers?.[idx]?.id;
      await AdminService.grantCompanyManagerAccess({ id });
      await fetchManagers();
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const removeManagerAccess = async (idx) => {
    try {
      setIsFetching(true);
      const id = managers?.[idx]?.id;
      await AdminService.removeCompanyManagerAccess({ id });
      await fetchManagers();
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const setPrimaryContact = async (idx) => {
    try {
      setIsFetching(true);
      const companyId = companies?.[currentDataIndex]?.id;
      const managerId = managers?.[idx]?.id;
      await AdminService.setManagerPrimaryContact({ companyId, managerId });
      await fetchManagers();
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (isEdit && isVisible && currentDataIndex !== null) {
      fetchLocations();
      fetchManagers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, isVisible, currentDataIndex]);

  // Initialize logo preview state based on mode
  useEffect(() => {
    if (isVisible) {
      if (isEdit) {
        if (companies?.[currentDataIndex]?.logo) {
          setIsEditingInitialLogo(true);
        } else {
          setIsEditingInitialLogo(false);
        }
        setIsLogoRemoved(false);
      }
      if (isAdd) {
        setIsEditingInitialLogo(false);
        setIsLogoRemoved(false);
      }
    }
  }, [isEdit, isAdd, isVisible]);

  const openAddLocation = () => {
    setEditingLocationIndex(null);
    setIsLocationPanelVisible(true);
  };

  const openEditLocation = (idx) => {
    setEditingLocationIndex(idx);
    setIsLocationPanelVisible(true);
  };

  const saveLocation = async (values) => {
    try {
      setIsFetching(true);
      const companyId = companies?.[currentDataIndex]?.id;
      const payload = {
        name: values.name,
        address: values.address,
        companyId,
        postCode: values.postCode || null,
        country: values.country?.value || values.country,
      };
      if (editingLocationIndex === null || editingLocationIndex === undefined) {
        await AdminService.createLocation(payload);
      } else {
        const id = locations?.[editingLocationIndex]?.id;
        await AdminService.updateLocation({ id, ...payload });
      }
      await fetchLocations();
      showModal({
        title: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successTitle'
        ),
        text: t(
          'dashboardComponents.Console.CompanyManagement.addEdit.successMessageEdit'
        ),
        dismissButtonLabel: t('common.gotIt'),
        dismissButtonVariant: ' ',
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  const deleteLocation = async (idx) => {
    try {
      setIsFetching(true);
      const id = locations?.[idx]?.id;
      await AdminService.deleteLocation({ id });
      await fetchLocations();
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }
  };

  // Reset form on close; we intentionally ignore formik to avoid infinite loop
  useEffect(() => {
    if (!isVisible) {
      formik.resetForm();
      setIsTriedToSubmit(false);
      setLogoFile(null);
      setIsEditingInitialLogo(true);
      setIsLogoRemoved(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const onClose = () => {
    setIsTriedToSubmit(false);
    handleClose();
  };

  const resetForm = () => {
    formik.resetForm();
    setIsTriedToSubmit(false);
  };

  return (
    <SidePopup
      fields={[...yupSchemaToFields(validationSchema)]}
      formik={{
        ...formik,
        dirty: formik.dirty || Boolean(logoFile) || isLogoRemoved,
      }}
      mode={isEdit ? 'edit' : 'add'}
      isVisible={isVisible}
      handleClose={onClose}
      title={
        isEdit
          ? t('dashboardComponents.Console.CompanyManagement.addEdit.titleEdit')
          : t('dashboardComponents.Console.CompanyManagement.addEdit.titleAdd')
      }
      leftButtonLabel={isAdd ? t('common.clear') : t('common.reset')}
      rightButtonLabel={isAdd ? t('common.add') : t('common.save')}
      onLeftButtonClick={resetForm}
      onRightButtonClick={() => {
        setIsTriedToSubmit(true);
        formik.handleSubmit();
      }}
      isRightButtonDisabled={(() => {
        const hasEmptyRequired = Object.values(formik.values).some(
          (val) => !val
        );
        // enable save if logo changed (selected or removed) in edit mode
        const logoChanged = Boolean(logoFile) || isLogoRemoved;
        if (isEdit && logoChanged) return isCreatingOrUpdating;
        return hasEmptyRequired || isCreatingOrUpdating;
      })()}
      switchNext={isEdit && selectedRows.length > 1 && switchToNextDataElement}
      switchPrev={isEdit && selectedRows.length > 1 && switchToPrevDataElement}
      hasNoAnimation={hasNoAnimation}
    >
      <div className={classes.AddCompany}>
        <div className={classes.row}>
          <div className={classes.col}>
            <div className={classes.row}>
              <div className={classes.col}>
                <FormDropdown
                  label={t(
                    'dashboardComponents.Console.CompanyManagement.addEdit.labels.Status'
                  )}
                  value={formik.values.status}
                  height={50}
                  touched={isTriedToSubmit}
                  onBlur={formik.handleBlur}
                  name="status"
                  error={formik.errors.status}
                  setFieldValue={formik.setFieldValue}
                  options={statusOptions}
                  placeholder={t(
                    'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Status'
                  )}
                />
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.col}>
                <FormDropdown
                  label={t(
                    'dashboardComponents.Console.CompanyManagement.addEdit.labels.Type'
                  )}
                  value={formik.values.type}
                  height={50}
                  touched={isTriedToSubmit}
                  onBlur={formik.handleBlur}
                  name="type"
                  error={formik.errors.type}
                  setFieldValue={formik.setFieldValue}
                  options={typeOptions}
                  placeholder={t(
                    'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Type'
                  )}
                />
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.col}>
                <TextInput
                  label={t(
                    'dashboardComponents.Console.CompanyManagement.addEdit.labels.Company Name'
                  )}
                  placeholder={t(
                    'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Company Name here'
                  )}
                  value={formik.values.name}
                  height={50}
                  touched={isTriedToSubmit}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="name"
                  error={formik.errors.name}
                />
              </div>
            </div>
          </div>
          <div className={classes.col}>
            <div className={classes.logoSection}>
              <span>
                {t(
                  'dashboardComponents.Console.CompanyManagement.addEdit.labels.Logo'
                )}
              </span>
              {logoFile || (isEdit && isEditingInitialLogo) ? (
                <div className={classes.imageContainer}>
                  <i
                    className={classes.deleteIcon}
                    onClick={() => {
                      if (isEditingInitialLogo) {
                        setIsEditingInitialLogo(false);
                        setIsLogoRemoved(true);
                      } else {
                        setLogoFile(null);
                        setIsLogoRemoved(true);
                      }
                    }}
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.00024 3.99582L8.78828 0.207778C9.06532 -0.0692594 9.51449 -0.0692595 9.79153 0.207778C10.0686 0.484816 10.0686 0.933983 9.79153 1.21102L6.00348 4.99906L9.79282 8.7884C10.0699 9.06544 10.0699 9.5146 9.79282 9.79164C9.51578 10.0687 9.06661 10.0687 8.78957 9.79164L5.00024 6.00231L1.21151 9.79104C0.934471 10.0681 0.485304 10.0681 0.208267 9.79104C-0.0687712 9.514 -0.0687711 9.06483 0.208267 8.7878L3.997 4.99906L0.209556 1.21162C-0.067482 0.934586 -0.0674817 0.485419 0.209556 0.208381C0.486594 -0.0686567 0.93576 -0.068657 1.2128 0.208381L5.00024 3.99582Z"
                        fill="white"
                      />
                    </svg>
                  </i>
                  {companies?.[currentDataIndex]?.logo || logoFile ? (
                    <img
                      src={
                        isEditingInitialLogo
                          ? getResizedImageLogoUrl({
                              url: `company/${companies?.[currentDataIndex]?.logo}`,
                              width: 150,
                            })
                          : URL.createObjectURL(logoFile)
                      }
                      alt={formik.values.name}
                    />
                  ) : null}
                </div>
              ) : (
                <div
                  className={classes.imageUploader}
                  onClick={() => fileInputRef.current.click()}
                >
                  <div className={classes.plusIcon}>
                    <svg
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.998 4.16797V15.8346"
                        stroke="white"
                        strokeWidth="1.64103"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.16431 10H16.831"
                        stroke="white"
                        strokeWidth="1.64103"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className={classes.uploaderContainer}>
                    <p>
                      {t(
                        'dashboardComponents.ProfileSettings.BrandKit.LeftPanel.SettingsContainer.Logo.logoTitle'
                      )}
                    </p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={(event) => {
                        setLogoFile(event.target.files[0]);
                        // switch off initial logo preview
                        setIsEditingInitialLogo(false);
                        setIsLogoRemoved(false);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <FormDropdown
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Industry'
              )}
              value={formik.values.industry}
              height={50}
              touched={isTriedToSubmit}
              onBlur={formik.handleBlur}
              name="industry"
              error={formik.errors.industry}
              setFieldValue={formik.setFieldValue}
              fetchOptions={{
                dataName: 'industriesBasicInfo',
                getDataHandler: AdminService.getIndustries,
                fetchHanlderOptions: {
                  sortOrder: 'asc',
                  orderBy: 'sortOrder',
                },
              }}
              placeholder={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Industry'
              )}
            />
          </div>
          <div className={classes.col}>
            <FormDropdown
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Country'
              )}
              value={formik.values.country}
              height={50}
              touched={isTriedToSubmit}
              onBlur={formik.handleBlur}
              name="country"
              error={formik.errors.country}
              setFieldValue={formik.setFieldValue}
              options={COUNTRY_OPTIONS}
              placeholder={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Country'
              )}
            />
          </div>
        </div>
        <div className={classes.row}>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Registered Seat'
              )}
              value={formik.values.address}
              height={50}
              touched={isTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="address"
              error={formik.errors.address}
              placeholder={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Address here'
              )}
            />
          </div>
          <div className={classes.col}>
            <TextInput
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.VAT'
              )}
              placeholder={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.VAT here'
              )}
              value={formik.values.vatId}
              height={50}
              touched={isTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="vatId"
              error={formik.errors.vatId}
            />
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.col}>
            <Textarea
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Company Joining Note'
              )}
              value={formik.values.companyJoiningNote}
              height={100}
              touched={isTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="companyJoiningNote"
              error={formik.errors.companyJoiningNote}
              placeholder={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Company Joining Note here'
              )}
            />
          </div>
        </div>

        <div className={classes.row}>
          <div className={classes.col}>
            <Textarea
              label={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.labels.Company Description'
              )}
              value={formik.values.companyDescription}
              height={100}
              touched={isTriedToSubmit}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="companyDescription"
              error={formik.errors.companyDescription}
              placeholder={t(
                'dashboardComponents.Console.CompanyManagement.addEdit.placeholders.Company Description here'
              )}
            />
          </div>
        </div>

        {isEdit && (
          <>
            <div className={classes.row}>
              <div className={classes.col}>
                <div className={classes.managersHeader}>
                  <span>
                    {t(
                      'dashboardComponents.Console.CompanyManagement.addEdit.labels.Managers'
                    )}
                  </span>
                  <div className={classes.addIcon} onClick={openAddManager}>
                    <svg
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.998 4.16797V15.8346"
                        stroke="white"
                        strokeWidth="1.64103"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.16431 10H16.831"
                        stroke="white"
                        strokeWidth="1.64103"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.col}>
                <div className={classes.managersBlock}>
                  {(managers || []).map((mgr, idx) => (
                    <div
                      className={classes.managerItem}
                      key={mgr.id || `${idx}`}
                    >
                      <div className={classes.cell}>
                        <span className={classes.cellLabel}>
                          {t(
                            'dashboardComponents.Console.CompanyManagement.addEdit.labels.Manager Name'
                          )}
                        </span>
                        <span className={classes.cellValue}>
                          {mgr.firstName || '-'} {mgr.lastName || '-'}{' '}
                          {mgr?.designation && (
                            <span>
                              <br />({mgr?.designation})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className={classes.cell}>
                        <span className={classes.cellLabel}>
                          {t(
                            'dashboardComponents.Console.CompanyManagement.addEdit.labels.Manager Contact'
                          )}
                        </span>
                        <span className={classes.cellValue}>
                          {mgr?.email || '-'}
                          <br />
                          {mgr?.phone || '-'}
                        </span>
                      </div>
                      <div className={classes.cell}>
                        <span className={classes.cellLabel}>
                          {t(
                            'dashboardComponents.Console.CompanyManagement.addEdit.labels.Access'
                          )}
                        </span>
                        <span className={classes.cellValue}>
                          {mgr?.user ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className={classes.cell}>
                        <span className={classes.cellLabel}>
                          {t(
                            'dashboardComponents.Console.CompanyManagement.addEdit.labels.Primary Contact'
                          )}
                        </span>
                        <span className={classes.cellValue}>
                          {mgr.isPrimaryContact ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className={classes.actions}>
                        <KebabMenu
                          options={[
                            {
                              label: t('common.edit'),
                              value: 'edit',
                              onClick: () => openEditManager(idx),
                            },
                            {
                              label: mgr?.user
                                ? t(
                                    'dashboardComponents.Console.CompanyManagement.managers.actions.removeAccess'
                                  )
                                : t(
                                    'dashboardComponents.Console.CompanyManagement.managers.actions.grantAccess'
                                  ),
                              value: mgr?.user ? 'removeAccess' : 'grantAccess',
                              onClick: () =>
                                mgr?.user
                                  ? removeManagerAccess(idx)
                                  : grantManagerAccess(idx),
                            },
                            {
                              label: t(
                                'dashboardComponents.Console.CompanyManagement.addEdit.actions.makePrimaryContact'
                              ),
                              value: 'makePrimary',
                              onClick: () => setPrimaryContact(idx),
                              withDivider: true,
                            },
                            {
                              label: t('common.delete'),
                              value: 'delete',
                              onClick: () => deleteManager(idx),
                              danger: true,
                            },
                          ]}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {isEdit && (
          <>
            <div className={classes.row}>
              <div className={classes.col}>
                <div className={classes.locationsHeader}>
                  <span>
                    {t(
                      'dashboardComponents.Console.CompanyManagement.addEdit.labels.Locations'
                    )}
                  </span>
                  <div className={classes.addIcon} onClick={openAddLocation}>
                    <svg
                      width="21"
                      height="20"
                      viewBox="0 0 21 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.998 4.16797V15.8346"
                        stroke="white"
                        strokeWidth="1.64103"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.16431 10H16.831"
                        stroke="white"
                        strokeWidth="1.64103"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.row}>
              <div className={classes.col}>
                <div className={classes.locationsBlock}>
                  {(locations || []).map((loc, idx) => (
                    <div
                      className={classes.locationItem}
                      key={loc.id || `${idx}`}
                    >
                      <div className={classes.cell}>
                        <span className={classes.cellLabel}>
                          {t(
                            'dashboardComponents.Console.CompanyManagement.addEdit.labels.Location Name'
                          )}
                        </span>
                        <span className={classes.cellValue}>
                          {loc.name || '-'}
                        </span>
                      </div>
                      <div className={classes.cell}>
                        <span className={classes.cellLabel}>
                          {t(
                            'dashboardComponents.Console.CompanyManagement.addEdit.labels.Address'
                          )}
                        </span>
                        <span className={classes.cellValue}>
                          {loc.address || '-'}
                        </span>
                      </div>
                      <div className={classes.cell}>
                        <span className={classes.cellLabel}>
                          {t(
                            'dashboardComponents.Console.CompanyManagement.addEdit.labels.Country'
                          )}
                        </span>
                        <span className={classes.cellValue}>
                          {loc.country || '-'}
                        </span>
                      </div>
                      <div className={classes.actions}>
                        {!loc.isSeat && (
                          <KebabMenu
                            options={[
                              {
                                label: t('common.edit'),
                                value: 'edit',
                                onClick: () => openEditLocation(idx),
                              },
                              {
                                label: t('common.delete'),
                                value: 'delete',
                                onClick: () => deleteLocation(idx),
                                danger: true,
                              },
                            ]}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <ManagerSidePanel
        isVisible={isManagerPanelVisible}
        handleClose={() => setIsManagerPanelVisible(false)}
        isEdit={
          editingManagerIndex !== null && editingManagerIndex !== undefined
        }
        initialValues={
          editingManagerIndex !== null && editingManagerIndex !== undefined
            ? managers?.[editingManagerIndex] || {}
            : {}
        }
        onSave={saveManager}
      />
      <LocationSidePanel
        isVisible={isLocationPanelVisible}
        handleClose={() => setIsLocationPanelVisible(false)}
        isEdit={
          editingLocationIndex !== null && editingLocationIndex !== undefined
        }
        initialValues={
          editingLocationIndex !== null && editingLocationIndex !== undefined
            ? locations?.[editingLocationIndex] || {}
            : {}
        }
        onSave={saveLocation}
      />
    </SidePopup>
  );
}
